import type { ReactNode } from "react";
import { Navigate, useParams } from "react-router";
import { AdminDashboardPage } from "@app/pages/admin-dashboard/admin-dashboard-page";
import { AdminEventDetailPage } from "@app/pages/admin-event-detail/admin-event-detail-page";
import { EventInReviewPage } from "@app/pages/event-in-review/event-in-review-page";
import { ClientsPage } from "@app/pages/clients/clients-page";
import { DashboardPage } from "@app/pages/dashboard/dashboard-page";
import { EventBuilderPage } from "@app/pages/event-builder/event-builder-page";
import { EventDetailPage } from "@app/pages/event-detail/event-detail-page";
import { GroupDetailPage } from "@app/pages/group-detail/group-detail-page";
import { HomePage } from "@app/pages/home/home-page";
import { InvitationPage } from "@app/pages/invitation/invitation-page";
import { LoginPage } from "@app/pages/login/login-page";
import { ManageEventsPage } from "@app/pages/manage-events/manage-events-page";
import { NotFoundPage } from "@app/pages/not-found/not-found-page";
import { OrganizersPage } from "@app/pages/organizers/organizers-page";
import { SettingsPage } from "@app/pages/settings/settings-page";
import { SignupPage } from "@app/pages/signup/signup-page";
import { VerifyEmailPage } from "@app/pages/verify-email/verify-email-page";
import { isDemoMode } from "@app/lib/app-mode";
import { PERMISSIONS } from "@app/lib/rbac/permissions";
import { ROLE_LABELS, type RoleLabel } from "@app/lib/role-constants";

export type RouteAuthPolicy = "public" | "protected";

export type AppRouteDefinition = {
  path: string;
  policy: RouteAuthPolicy;
  element: ReactNode;
  requiredPermission?: string;
  allowedRoles?: RoleLabel[];
};

function DemoOnlyRoute({ children }: { children: ReactNode }) {
  return isDemoMode() ? children : <Navigate to="/admin/dashboard" replace />;
}

function LegacyGroupDetailRoute() {
  const { groupId } = useParams();

  return isDemoMode() ? (
    <GroupDetailPage />
  ) : (
    <Navigate
      to={groupId ? `/admin/groups/${groupId}` : "/admin/dashboard"}
      replace
    />
  );
}

export const appRouteConfig: AppRouteDefinition[] = [
  {
    path: "/",
    policy: "public",
    element: <HomePage />,
  },
  {
    path: "/login",
    policy: "public",
    element: <LoginPage />,
  },
  {
    path: "/signup",
    policy: "public",
    element: <SignupPage />,
  },
  {
    path: "/verify-email",
    policy: "public",
    element: <VerifyEmailPage />,
  },
  {
    path: "/otc",
    policy: "public",
    element: <Navigate to="/login" replace />,
  },
  {
    path: "/manage-events",
    policy: "protected",
    element: <ManageEventsPage />,
    allowedRoles: [ROLE_LABELS.ORGANIZER],
  },
  {
    path: "/event-submitted",
    policy: "protected",
    element: <EventInReviewPage />,
    allowedRoles: [ROLE_LABELS.ORGANIZER, ROLE_LABELS.ADMIN],
  },
  {
    path: "/events/:eventId",
    policy: "protected",
    element: <EventDetailPage />,
    allowedRoles: [ROLE_LABELS.ORGANIZER],
  },
  {
    path: "/events/:eventId/edit",
    policy: "protected",
    element: <EventBuilderPage />,
    allowedRoles: [ROLE_LABELS.ORGANIZER],
  },
  {
    path: "/dashboard",
    policy: "protected",
    element: (
      <DemoOnlyRoute>
        <DashboardPage />
      </DemoOnlyRoute>
    ),
    requiredPermission: PERMISSIONS.DASHBOARD_VIEW,
    allowedRoles: [ROLE_LABELS.ADMIN],
  },
  {
    path: "/clients",
    policy: "protected",
    element: (
      <DemoOnlyRoute>
        <ClientsPage />
      </DemoOnlyRoute>
    ),
    requiredPermission: PERMISSIONS.CLIENT_MANAGE,
    allowedRoles: [ROLE_LABELS.ADMIN],
  },
  {
    path: "/organizers",
    policy: "protected",
    element: (
      <DemoOnlyRoute>
        <OrganizersPage />
      </DemoOnlyRoute>
    ),
    requiredPermission: PERMISSIONS.ORGANIZER_MANAGE,
    allowedRoles: [ROLE_LABELS.ADMIN],
  },
  {
    path: "/organizers/:organizerId/groups/:groupId",
    policy: "protected",
    element: <LegacyGroupDetailRoute />,
    requiredPermission: PERMISSIONS.ORGANIZER_MANAGE,
    allowedRoles: [ROLE_LABELS.ADMIN],
  },
  {
    path: "/admin/dashboard",
    policy: "protected",
    element: <AdminDashboardPage />,
    requiredPermission: PERMISSIONS.DASHBOARD_VIEW,
    allowedRoles: [ROLE_LABELS.ADMIN],
  },
  {
    path: "/admin/groups/:groupId",
    policy: "protected",
    element: <AdminEventDetailPage />,
    requiredPermission: PERMISSIONS.ORGANIZER_MANAGE,
    allowedRoles: [ROLE_LABELS.ADMIN],
  },
  {
    path: "/settings",
    policy: "protected",
    element: (
      <DemoOnlyRoute>
        <SettingsPage />
      </DemoOnlyRoute>
    ),
    requiredPermission: PERMISSIONS.SETTINGS_EDIT,
    allowedRoles: [ROLE_LABELS.ADMIN],
  },
  {
    path: "/events/:eventId/landing-page",
    policy: "public",
    element: <InvitationPage />,
  },
  {
    path: "*",
    policy: "public",
    element: <NotFoundPage />,
  },
];
